import React, { useEffect, useMemo, useState } from 'react';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import DocViewer from '@cyntler/react-doc-viewer';
import dayjs from 'dayjs';

import { selectIncident, useAppSelector } from 'slices/commonSelectors';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import { NotebookFile } from 'components/FileManagement/hooks/useGetAllFiles';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useSearchParams } from 'react-router';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';

const InitialIncidentBriefing: React.FC = () => {
  const currentIncident = useAppSelector(selectIncident);
  const { data: incidentInfo } = useIncidentInfo();
  const downloadFile = useDownloadFile();
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const { exportAsForm, previewFromSignedUrl } = useIcsFormPdfPreview();
  const { data: exportedForms = [] } = useExportedFormFiles(
    currentIncident?.id
  );

  const [displayDocuments, setDisplayDocuments] = useState<
    { uri: string; fileName: string }[] | undefined
  >();

  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-initial-incident-briefing`;

  const findIcs201Op0File = useMemo(() => {
    // Heuristic: match ICS 201 and OP0 regardless of separators/case
    const regex201 = /ICS[-_ ]?201/i;
    const regexOp0 = /OP[-_ ]?0/i;
    return (files: NotebookFile[]) =>
      files
        .filter((f) => regex201.test(f.file_name) && regexOp0.test(f.file_name))
        .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
        .at(0);
  }, []);

  const currentFile = useMemo(
    () => findIcs201Op0File(exportedForms as NotebookFile[]),
    [exportedForms, findIcs201Op0File]
  );

  useEffect(() => {
    if (!currentFile?.fileUrl) {
      queueMicrotask(() => setDisplayDocuments(undefined));
      return;
    }
    getSignedUrl(currentFile.fileUrl, {
      onSuccess: (url) => {
        setDisplayDocuments([{ uri: url, fileName: currentFile.file_name }]);
      },
    });
  }, [currentFile, getSignedUrl]);

  const handlePreview = () => {
    const doc = displayDocuments?.[0];
    if (!doc || !currentFile) return;
    previewFromSignedUrl({
      fileUrl: doc.uri,
      fileName: doc.fileName,
      onDownload: () => {
        void downloadFile(currentFile);
      },
    });
  };

  const handleExport = () => {
    void exportAsForm({
      formname: 'ICS_201-CG',
      data: {},
      mapSketch: incidentInfo?.mapSketchUrl ?? null,
      periodNumber: 0,
    });
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget w-100"
      contentClassName="content mt-2"
      headerLeft={
        <h3 className="m-0">
          <SharedIcon
            iconName="description"
            classes="me-2"
            size="md"
            color="primary"
          />
          {getIncidentCardTitle('initialResponse')}
        </h3>
      }
      headerRight={
        displayDocuments ? (
          <StylishNewButton
            onClick={(e) => {
              e.stopPropagation();
              handlePreview();
            }}
            className="button button--primary"
          >
            <SharedIcon iconName="visibility" classes="me-2" size="sm" />{' '}
            Preview ICS-201
          </StylishNewButton>
        ) : (
          <StylishNewButton
            className="button button--secondary"
            onClick={(e) => {
              e.stopPropagation();
              handleExport();
            }}
          >
            <SharedIcon iconName="download" classes="me-2" size="sm" /> Generate
            ICS-201
          </StylishNewButton>
        )
      }
    >
      {displayDocuments ? (
        <DocViewer documents={displayDocuments} />
      ) : (
        <div className="section-main d-flex flex-column align-items-center">
          <div className="d-flex justify-content-center">
            <SharedIcon iconName="description" classes="me-2" size="lg" />
          </div>
          <div>
            <div className="fs-5 mb-2">
              {getIncidentCardTitle('initialResponse')}
            </div>
            <div className="mb-2">
              No exported ICS-201 was found. Export one to display it here.
            </div>
          </div>
        </div>
      )}
    </ConditionalCollapsibleCard>
  );
};

export default InitialIncidentBriefing;
